import React, {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";

// Components
import Accordion from "../../components/Accordion.js";
import Modal from "../../components/Modal.js";

// Style
import {bp} from "../../style/components/breakpoints.js";
import {border, pad, radius} from "../../style/components/variables.js";
import {
  Heading,
  Text,
  Small,
  Button,
  Inline,
  FormGroup,
  Select as NativeSelect
} from "../../style/components/general.js";

const ModalAddState = ({visible, setVisible, facilityStates, checksheetStates, onConfirm}) => {
  const options = useMemo(() => {
    if (!facilityStates?.length) return [];
    return facilityStates
      .filter(({id}) => !checksheetStates?.[`${id}`])
      .map(({name, id, color}) => ({
        label: name,
        name: `${id}`,
        color
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [facilityStates, checksheetStates]);

  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    if (visible && options.length) setSelectedId(`${options[0].name}`);
  }, [visible, options]);

  const selected = options.find(o => `${o.name}` === `${selectedId}`);

  const handleAdd = () => {
    if (!selectedId) return;
    onConfirm(`${selectedId}`);
    setVisible(false);
  };

  return (
    <Modal visible={visible} setVisible={setVisible} width="500px">
      <Heading>Checksheet State</Heading>
      <FormGroup>
        <DescriptionSection>
          <Text>
            <strong>Checksheet states</strong> give this checksheet a separate builder, frequency,
            and rules when a specific facility state applies—for example, a different zone, season,
            or operating mode.
          </Text>
          <AccordionBlock>
            <Accordion testId="modal-add-state-details" label={<Small>Learn More</Small>} wrap>
              <ExpandableContent>
                <DetailList>
                  <li>
                    <Text small quiet>
                      <strong>Why they exist.</strong> Facility states describe your site&rsquo;s
                      contexts. Checksheet states reuse those to branch this one checksheet—so the
                      right fields, logic, and timing apply when each facility state is active.
                    </Text>
                  </li>
                  <li>
                    <Text small quiet>
                      <strong>Not the same as facility settings.</strong> This only changes how{" "}
                      <em>this</em> checksheet behaves; it does not modify facility state
                      definitions elsewhere.
                    </Text>
                  </li>
                  <li>
                    <Text small quiet>
                      <strong>When to add a state.</strong> You can add a state before building out
                      the checksheet and configure each variant independently, or add it after the
                      default checksheet is built—the new state starts as a copy of your current
                      configuration.
                    </Text>
                  </li>
                  <li>
                    <Text small quiet>
                      <strong>Linked fields.</strong> Fields present when a state is added are linked
                      across both variants: the field appears on each state, and responses
                      submitted under either version of the checksheet are associated with the same
                      field. Fields added to only one variant afterward remain unique to that state.
                    </Text>
                  </li>
                </DetailList>
              </ExpandableContent>
            </Accordion>
          </AccordionBlock>
        </DescriptionSection>
        <Text quiet small>
          Pick an existing facility state below. The new state starts as a copy of your default
          configuration, which you can then modify independently.
        </Text>
      </FormGroup>
      {options.length === 0 ? (
        <>
          <Text bold role="status">
            No additional facility states are available to add. Every eligible state may already
            have a checksheet state, or your facility has no more states to choose from.
          </Text>
          <Footer end>
            <Button type="button" onClick={() => setVisible(false)}>
              Close
            </Button>
          </Footer>
        </>
      ) : (
        <>
          <FormGroup>
            <StateDropdown
              id="modal-add-state-facility"
              name="modal-add-state-facility"
              fullWidth
              $hex={selected?.color ? `#${selected.color}` : undefined}
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              aria-label="Facility state">
              {options.map(o => (
                <option
                  key={o.name}
                  value={o.name}
                  style={o.color ? {color: `#${o.color}`} : undefined}>
                  {o.label}
                </option>
              ))}
            </StateDropdown>
          </FormGroup>
          <Footer end>
            <Button type="button" onClick={() => setVisible(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleAdd} disabled={!selectedId}>
              Add State
            </Button>
          </Footer>
        </>
      )}
    </Modal>
  );
};

ModalAddState.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  facilityStates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string
    })
  ),
  checksheetStates: PropTypes.object.isRequired,
  onConfirm: PropTypes.func.isRequired
};

// Style Overrides
const Footer = styled(Inline)`
  margin-top: ${pad * 1.5}px;
  justify-content: flex-end;
`;

const DescriptionSection = styled.div`
  margin-bottom: ${pad * 1.5}px;
  padding: ${pad * 1.2}px ${pad * 1.4}px;
  border-radius: ${radius};
  background: ${({theme}) => `${theme.secondary}10`};
  border: ${border} solid ${({theme}) => `${theme.secondary}30`};
`;

const AccordionBlock = styled.div`
  width: 100%;
  margin-top: ${pad * 0.5}px;
`;

const ExpandableContent = styled.div`
  margin-left: ${pad}px;
  padding: 0 0 ${pad}px ${pad * 1.5}px;
  margin-top: -${pad / 2}px;
  border-left: ${border} solid ${({theme}) => theme.primary};
`;

const DetailList = styled.ul`
  margin: 0;
  padding: 0 0 0 ${pad * 1.8}px;

  li {
    margin-bottom: ${pad * 0.8}px;
  }

  li:last-child {
    margin-bottom: 0;
  }
`;

const StateDropdown = styled(NativeSelect)`
  width: 100%;
  min-width: 0;
  max-width: 100%;

  ${bp(3)} {
    max-width: 100%;
  }

  ${({$hex, theme}) =>
    $hex
      ? css`
          color: ${$hex};
        `
      : css`
          color: ${theme.secondary};
        `}
`;

export default ModalAddState;
